/**
 * Seeded Random Number Generator
 *
 * Uses the mulberry32 algorithm to produce deterministic random sequences.
 * Given the same seed, the same sequence of numbers is always produced.
 *
 * Used to synchronize random selections (audio, video) between primary host
 * and mirror hosts — both receive the same seed from the server schema
 * and produce identical random sequences.
 *
 * Category-based instances prevent call-order dependencies between
 * independent random systems (audio vs video vs messages).
 */

/**
 * Mulberry32 PRNG - fast, simple, good distribution
 * @param {number} seed - 32-bit integer seed
 * @returns {function} Function that returns next random float [0, 1)
 */
function mulberry32(seed) {
    let state = seed | 0;
    return function () {
        state = (state + 0x6D2B79F5) | 0;
        let t = Math.imul(state ^ (state >>> 15), 1 | state);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

export class SeededRandom {
    /**
     * @param {number} seed - Integer seed value
     */
    constructor(seed) {
        this._seed = seed;
        this._rng = mulberry32(seed);
    }

    /** Get the seed this instance was created with */
    get seed() {
        return this._seed;
    }

    /**
     * Next random float in [0, 1) — drop-in replacement for Math.random()
     * @returns {number}
     */
    next() {
        return this._rng();
    }

    /**
     * Random integer in [0, max)
     * @param {number} max - Exclusive upper bound
     * @returns {number}
     */
    nextInt(max) {
        return Math.floor(this._rng() * max);
    }

    /**
     * Pick a random element from an array
     * @param {Array} array
     * @returns {*}
     */
    pick(array) {
        if (!array || array.length === 0) return null;
        return array[this.nextInt(array.length)];
    }

    /**
     * Fisher-Yates shuffle (returns a new array)
     * @param {Array} array
     * @returns {Array} New shuffled array
     */
    shuffle(array) {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = this.nextInt(i + 1);
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    }
}

/**
 * Create category-specific SeededRandom instances from a base seed.
 * Each category gets a different derived seed so that audio randomness
 * doesn't affect video sequence alignment (and vice versa).
 *
 * @param {number} baseSeed - The seed from the server schema
 * @returns {{ audio: SeededRandom, video: SeededRandom, vo: SeededRandom }}
 */
export function createSeededRandoms(baseSeed) {
    return {
        audio: new SeededRandom(baseSeed + 1),
        video: new SeededRandom(baseSeed + 2),
        vo: new SeededRandom(baseSeed + 3),
    };
}
